<template>
    <SideFormSection>
        <template #title> Users </template>
        <template #subtitle>
            Manage company users and pending invites in one place.
        </template>
        <template #rawContent>
            <Card
                title="Users"
                iconName="users"
            >
                <template #content>
                    <div class="space-y-6 py-6">
                        <div class="space-y-3">
                            <div class="text-sm font-medium text-gray-700">
                                Current Users
                            </div>
                            <Table>
                                <TableHead>
                                    <TableHeadCell>Name</TableHeadCell>
                                    <TableHeadCell>Email</TableHeadCell>
                                    <TableHeadCell>Role</TableHeadCell>
                                    <TableHeadCell></TableHeadCell>
                                </TableHead>
                                <TableBody>
                                    <TableBodyRow
                                        v-for="companyUserAggregate in localCompanyUserAggregates"
                                        :key="
                                            companyUserAggregate.companyUser.id
                                                .value
                                        "
                                    >
                                        <TableBodyRowCell>
                                            {{
                                                companyUserAggregate.user.getFullName()
                                            }}
                                        </TableBodyRowCell>
                                        <TableBodyRowCell>
                                            {{
                                                companyUserAggregate.user.email
                                            }}
                                        </TableBodyRowCell>
                                        <TableBodyRowCell>
                                            {{
                                                companyUserAggregate.companyUser
                                                    .role
                                            }}
                                        </TableBodyRowCell>
                                        <TableBodyRowCell
                                            class="flex justify-end gap-1"
                                        >
                                            <Button
                                                as="a"
                                                :href="
                                                    urlFactory.makeCompanyUserEditPageUrl(
                                                        companyUserAggregate
                                                            .companyUser.id,
                                                    )
                                                "
                                                color="light"
                                                size="small"
                                                iconName="edit"
                                            >
                                                Edit
                                            </Button>
                                            <DeleteCompanyUserButtonModal
                                                :companyUser="
                                                    companyUserAggregate.companyUser
                                                "
                                                @onSuccess="
                                                    handleCompanyUserDeleted(
                                                        companyUserAggregate.companyUser,
                                                    )
                                                "
                                            />
                                        </TableBodyRowCell>
                                    </TableBodyRow>
                                </TableBody>
                            </Table>
                        </div>

                        <div class="space-y-3">
                            <div class="text-sm font-medium text-gray-700">
                                Pending Invites
                            </div>
                            <Table>
                                <TableHead>
                                    <TableHeadCell>Email</TableHeadCell>
                                    <TableHeadCell>Sent At</TableHeadCell>
                                    <TableHeadCell></TableHeadCell>
                                </TableHead>
                                <TableBody>
                                    <TableBodyRow
                                        v-for="companyUserInvite in localCompanyUserInvites"
                                        :key="companyUserInvite.id.value"
                                    >
                                        <TableBodyRowCell>
                                            {{
                                                companyUserInvite.companyUserEmail
                                            }}
                                        </TableBodyRowCell>
                                        <TableBodyRowCell>
                                            {{
                                                formatCompanyUserInviteDate(
                                                    companyUserInvite.createdAt,
                                                )
                                            }}
                                        </TableBodyRowCell>
                                        <TableBodyRowCell
                                            class="flex justify-end gap-1"
                                        >
                                            <DeleteCompanyUserInviteButtonModal
                                                :companyUserInvite="
                                                    companyUserInvite
                                                "
                                                @onSuccess="
                                                    handleCompanyUserInviteDeleted(
                                                        companyUserInvite,
                                                    )
                                                "
                                            />
                                        </TableBodyRowCell>
                                    </TableBodyRow>
                                    <TableBodyRow
                                        v-if="
                                            localCompanyUserInvites.length === 0
                                        "
                                    >
                                        <TableBodyRowCell
                                            colspan="3"
                                            class="text-center text-gray-500"
                                        >
                                            No pending invites.
                                        </TableBodyRowCell>
                                    </TableBodyRow>
                                </TableBody>
                            </Table>
                        </div>

                        <div class="flex justify-end">
                            <CreateCompanyUserInviteButtonDrawer
                                @onSuccess="handleCompanyUserInviteCreated"
                            />
                        </div>
                    </div>
                </template>
            </Card>
        </template>
    </SideFormSection>
</template>

<script setup lang="ts">
    import { ref, type PropType } from 'vue'
    import CompanyUserAggregate from '@/App/CompanyUser/Entity/CompanyUserAggregate'
    import CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'
    import CompanyUserInvite from '@/App/CompanyUserInvite/Entity/CompanyUserInvite'
    import CreateCompanyUserInviteButtonDrawer from '@/App/CompanyUserInvite/Component/CreateCompanyUserInviteButtonDrawer.vue'
    import DeleteCompanyUserInviteButtonModal from '@/App/CompanyUserInvite/Component/DeleteCompanyUserInviteButtonModal.vue'
    import DeleteCompanyUserButtonModal from '@/App/CompanyUser/Component/DeleteCompanyUserButtonModal.vue'
    import SideFormSection from '@/Shared/Component/SideFormSection.vue'
    import Card from '@/Shared/Component/Card.vue'
    import Table from '@/Shared/Component/Table/Table.vue'
    import TableHead from '@/Shared/Component/Table/TableHead.vue'
    import TableHeadCell from '@/Shared/Component/Table/TableHeadCell.vue'
    import TableBody from '@/Shared/Component/Table/TableBody.vue'
    import TableBodyRow from '@/Shared/Component/Table/TableBodyRow.vue'
    import TableBodyRowCell from '@/Shared/Component/Table/TableBodyRowCell.vue'
    import Button from '@/Shared/Component/Button.vue'
    import useDate from '@/Shared/Hook/useDate'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    const props = defineProps({
        companyUserAggregates: {
            type: Array as PropType<CompanyUserAggregate[]>,
            required: true,
        },
        companyUserInvites: {
            type: Array as PropType<CompanyUserInvite[]>,
            required: true,
        },
    })

    const urlFactory = new UrlFactory()
    const localCompanyUserAggregates = ref<CompanyUserAggregate[]>(
        props.companyUserAggregates,
    )
    const localCompanyUserInvites = ref<CompanyUserInvite[]>(
        props.companyUserInvites,
    )
    const date = useDate()

    const formatCompanyUserInviteDate = (createdAt: string): string => {
        return date.dateTime(createdAt)
    }

    const handleCompanyUserDeleted = (deletedCompanyUser: CompanyUser) => {
        localCompanyUserAggregates.value =
            localCompanyUserAggregates.value.filter(
                (companyUserAggregate) =>
                    companyUserAggregate.companyUser.id !==
                    deletedCompanyUser.id,
            )
    }

    const handleCompanyUserInviteDeleted = (
        deletedCompanyUserInvite: CompanyUserInvite,
    ) => {
        localCompanyUserInvites.value = localCompanyUserInvites.value.filter(
            (companyUserInvite) =>
                companyUserInvite.id !== deletedCompanyUserInvite.id,
        )
    }

    const handleCompanyUserInviteCreated = (
        companyUserInvite: CompanyUserInvite,
    ) => {
        localCompanyUserInvites.value.unshift(companyUserInvite)
    }
</script>
